import { contact_info } from '@/utils/contact-info-dictionary'
import { avenAdvisorFreeCoffeePromoTermsAndConditionsPath } from 'aven_types'

export const ENGLISH_TRANSLATIONS = {
    pages: {
        marketing: {
            landing: {
                safeCreditScore: 'This will <span class="thick-underline">NOT</span> affect your credit score.',
            },
        },
        onboarding: {
            prefilledPersonalInfo: {
                disclosures: {
                    affiliateConsents:
                        '1. By clicking Continue, you consent to Credit Builder Inc. and its affiliates ' +
                        'obtaining your credit report from consumer reporting agencies to verify your information, help ' +
                        'you track your credit score, and inform you of products and services you might be eligible for. ' +
                        'You also agree to our <a target="_blank" href="https://www.aven.com/docs/ESIGNConsent.pdf">ESIGN Consent</a>, ' +
                        '<a target="_blank" href="https://www.aven.com/public/docs/TermsOfService">Terms of Service</a>, ' +
                        'and <a target="_blank" href="https://www.aven.com/docs/PrivacyPolicy.html">Privacy Policy</a>. ' +
                        'See also our <a href="https://www.aven.com/docs/ConsumerPrivacyPolicyNotice.pdf">Consumer Privacy Notice</a>.',
                    hausFootnote:
                        '2. By clicking Continue, you also receive a free lifetime membership to the Homeowners ' +
                        'Association of the United States (HAUS). HAUS is a nonprofit dedicated to promoting homeownership. You ' +
                        'may discontinue your HAUS membership at any time.',
                },
            },
            taxReturnAddress: {
                addressPrompt: 'What address did you use on your most recent tax return?',
                exactAddress: 'We need the exact address.',
            },
        },
    },
    components: {
        depositBalances: {
            header: 'Bank Accounts',
            empty: 'No bank accounts found',
        },
        formFieldAddress: {
            placeholder: {
                completeAddress: 'Street Address, City, State',
                street: 'Street Address',
                unit: 'Apt #',
                city: 'City',
                state: 'State',
                postalCode: 'Zip',
            },
            enterAddressManually: 'Enter address manually',
            validation: {
                addressNotFound: 'Address Not Found. Please check your input.',
                CAResidentOnly: 'Must be a resident of California',
            },
            googleError: 'Could not find valid location, please try again',
        },
        formSelectAutocomplete: {
            noOptions: 'No results found for',
            startSearching: 'Start typing to search',
        },
        formFieldSsn: {
            placeholder: 'Social Security Number',
            placeholderLast4: 'Last 4 of Social Security',
        },
        freeCoffeeCard: {
            // Split strings for terms and conditions links (to support native app click handling)
            starbucksInvitationAcceptance: 'By clicking above, you accept this Starbucks Coffee Promotion invitation and the Starbucks Coffee',
            dunkinInvitationAcceptance: "By clicking above, you accept this Dunkin' Coffee Promotion invitation and the Dunkin' Coffee",
            starbucksParticipationAcceptance: 'By participating, you accept the Starbucks Coffee',
            dunkinParticipationAcceptance: "By participating, you accept the Dunkin' Coffee",
            termsAndConditions: 'Terms and Conditions',
            starbucksSponsorDisclaimer: 'Starbucks is not a sponsor of this promotion.',
            dunkinSponsorDisclaimer: "Dunkin' is not a sponsor of this promotion.",
            // Legacy strings with embedded HTML (kept for backwards compatibility)
            starbucksInvitationAcceptanceAndTermsAndConditions: `By clicking above, you accept this Starbucks Coffee Promotion invitation and the Starbucks Coffee<a href="${avenAdvisorFreeCoffeePromoTermsAndConditionsPath}" target="_blank" class="tw-text-gray-300 hover:tw-text-gray-400 visited:tw-text-gray-300 tw-no-underline"> <span class="tw-underline">Terms and Conditions</span>. </a> Starbucks is not a sponsor of this promotion.`,
            dunkinInvitationAcceptanceAndTermsAndConditions: `By clicking above, you accept this Dunkin' Coffee Promotion invitation and the Dunkin' Coffee<a href="${avenAdvisorFreeCoffeePromoTermsAndConditionsPath}" target="_blank" class="tw-text-gray-300 hover:tw-text-gray-400 visited:tw-text-gray-300 tw-no-underline"> <span class="tw-underline">Terms and Conditions</span>. </a> Dunkin' is not a sponsor of this promotion.`,
            starbucksTermsAndConditionsAcceptance: `By participating, you accept the Starbucks Coffee <a href="${avenAdvisorFreeCoffeePromoTermsAndConditionsPath}" target="_blank" class="tw-text-gray-300 hover:tw-text-gray-400 visited:tw-text-gray-300 tw-no-underline"><span class="tw-underline">Terms and Conditions</span>. </a> Starbucks is not a sponsor of this promotion.`,
            dunkinTermsAndConditionsAcceptance: `By participating, you accept the Dunkin' Coffee <a href="${avenAdvisorFreeCoffeePromoTermsAndConditionsPath}" target="_blank" class="tw-text-gray-300 hover:tw-text-gray-400 visited:tw-text-gray-300 tw-no-underline"><span class="tw-underline">Terms and Conditions</span>. </a> Dunkin' is not a sponsor of this promotion.`,
            starbucksTrademarkDisclosure: 'The Starbucks word mark and Logo are trademarks of Starbucks Corporation.',
            dunkinTrademarkDisclosure: 'The Dunkin word mark and Logo are trademarks of Dunkin Brands Inc.',
        },
        freeCoffeeUpsellSuccessModal: {
            enjoyYourCoffee: 'Enjoy your coffee!',
            compactGiftCardOnTheWay: 'Your {promoItemName} gift card is on its way to your inbox.',
            largeGiftCardOnTheWay: 'Your {promoItemName} is on the way.',
            description: 'Click below to get your gift card. Be sure to check back every week to claim your weekly gift card.',
            getMyGiftCard: 'Get My Gift Card',
            shareWithFriends: 'Share With Friends',
            giftCardAltText: '{freeCoffeeType} gift card',
        },
        freeCoffeeClaimSuccessModal: {
            enjoyYourCoffee: 'Enjoy your coffee!',
            description: 'Click below to get your gift card. Be sure to check back every week to claim your weekly gift card.',
            getMyGiftCard: 'Get My Gift Card',
            shareWithFriends: 'Share With Friends',
            giftCardAltText: '{freeCoffeeType} gift card',
        },
        FreeCoffeeCardPolicified: {
            freeCoffeeConnectPlaidContent: {
                genericConnectPlaid: 'Connect your accounts with Plaid so that we can determine your eligibility.',
                connectPlaidToVerifyIncome: 'Connect your accounts with Plaid to verify that you have ${grossAnnualIncomeThreshold}+ income and qualify for the Free Coffee Every Week promotion.',
                connectPlaidAgainToVerifyIncome:
                    'We see that you connected an account through Plaid. However, our system could not verify that it is your main account. Please connect another deposit or checking account that you receive paychecks with.',
                connectPlaidToVerifyIncomeDynamicLegacyPolicyBanner:
                    "We've updated our Free Coffee Promotion to limit eligibility to those who have connected their primary bank account so you can get an accurate view of your finances on Aven Advisor. To be eligible, please connect at least one bank account to your Aven Advisor account.  The connected bank account must be one in which you deposit your paychecks.",
            },
            freeCoffeeGetCoffeeContent: {
                canClaimNow: 'Get a complimentary $5 {freeCoffeeCopy} card on us every week that your credit score<sup>†</sup> is {vantageScoreThreshold}+. Your credit score is {vantageScore}.',
                canClaimNowWithWeeksLeft: "Get {freeCoffeeCopy} on us for <span class='tw-font-bold'>{weeksOfFreeCoffeeLeft} more {remainingTimePeriodNoun}.</span>",
            },
            freeCoffeeCountdownContent: {
                claimingTooSoon: 'Get a $5 {freeCoffeeCopy} gift card every week that your credit score<sup>†</sup> is {vantageScoreThreshold}+. Your credit score is {vantageScore}.',
                claimingTooSoonWithWeeksLeft: "Get {freeCoffeeCopy} on us for <span class='tw-font-bold'>{weeksOfFreeCoffeeLeft} more {remainingTimePeriodNoun}.</span>",
            },
        },
        threeBigNumbers: {
            changePeriod: 'Past 3 Months',
            notAvailable: 'Not available',
        },
        recentlySoldHomes: {
            title: 'Recently Sold Homes',
            seeAllHomes: 'See All Homes',
            empty: 'No recently sold homes available',
        },
        subscriptionsCard: {
            title: 'Subscriptions',
            seeAll: 'See All Subscriptions',
            empty: 'No subscription data available',
            infoModalFallback: 'Information from your connected accounts.',
            updatedToday: 'Updated today.',
            details: {
                title: 'Details',
                paidFrom: 'Paid from',
                amount: 'Amount',
                firstPayment: 'First Payment',
                lastPayment: 'Last Payment',
                totalSpend: 'Total Spend',
            },
        },
    },
    validation: {
        alpha: 'May only contain alphabetic characters',
        alpha_num: 'May only contain alpha-numeric characters',
        alpha_dash: 'May contain alpha-numeric characters as well as dashes and underscores',
        alpha_spaces: 'May only contain alphabetic characters as well as spaces',
        between: 'Must be between {min} and {max}',
        confirmed: 'Confirmation does not match',
        digits: 'Must be numeric and exactly contain {length} digits',
        dimensions: 'Must be {width} pixels by {height} pixels',
        email: 'Must be a valid email',
        excluded: 'Is not a valid value',
        ext: 'Is not a valid file',
        image: 'Must be an image',
        invalidDate: 'Invalid date. Please use MM/DD/YYYY format',
        integer: 'Must be an integer',
        length: 'Must be {length} characters long',
        max_value: 'Must be {max} or less',
        max: 'May not be greater than {length} characters',
        mimes: 'Must have a valid file type',
        min_value: 'Must be greater than {min}',
        min: 'Must be at least {length} characters',
        numeric: 'May only contain numeric characters',
        oneOf: 'Is not a valid value',
        regex: 'Format is invalid',
        required_if: 'This field is required',
        required: 'This field is required',
        size: 'Size must be less than {size}KB',
    },
    customValidationRules: {
        CAResidentOnly: 'Must be a resident of California',
        twilioCode: 'Code must be exactly 4 digits',
        ssnLast4: 'Last 4 SSN digits are required',
        ssnFull9: 'Full 9 digit SSN is required',
        incompleteAddress: 'Incomplete address',
        underAge: 'You must be over {minAgeYears} years old',
        zeroOrNegativeAge: 'Please make sure your date of birth is correct and try again',
        notValidPhoneNumber: 'Not a valid phone number',
        invalidHomeLocationDueToFloodZone: 'Sorry, we do not currently offer our card in special flood hazard areas',
        rinCounty: "Sorry, our card is not currently available in your county. We'll reach out when it's ready!",
        emailAlreadyExists: 'An account already exists for this email address',
        phoneNumberAlreadyExists: 'An account already exists for this phone number',
        invalidCurrency: 'Please enter a valid amount',
        currencyTooSmall: 'Minimum allowed amount is {minAmount}',
        currencyTooLarge: 'Maximum allowed amount is {maxAmount}',
        passwordRequirements: 'Password must be at least 8 characters, with at least 1 number and 1 special character.',
        passwordMismatch: `The passwords you entered don't match`,
    },
    contactInfo: contact_info,
}
